/**
 * CreativeStyleSettings Component
 * 
 * Allows users to configure the writing style profile for creative projects.
 * Style settings are injected into Block 2 of the AI's 3-block prompt architecture
 * to maintain consistent prose style throughout the writing session.
 */

import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Label, 
  Textarea,
  RadixSelect,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui';
import { StyleProfile } from '@/types';

interface CreativeStyleSettingsProps {
  styleProfile?: StyleProfile;
  onChange: (styleProfile: StyleProfile) => void;
  disabled?: boolean;
}

// Preset style profiles for common genres
const STYLE_PRESETS: Record<string, StyleProfile> = {
  literary: {
    prose: 'literary',
    pacing: 'slow-burn',
    tension: 'medium',
    dialogue: 'balanced',
    description: 'rich',
  },
  thriller: {
    prose: 'thriller',
    pacing: 'fast-paced',
    tension: 'high',
    dialogue: 'sparse',
    description: 'minimal',
  },
  cozy: {
    prose: 'cozy',
    pacing: 'slow-burn',
    tension: 'low',
    dialogue: 'dialogue-heavy',
    description: 'moderate',
  },
  commercial: {
    prose: 'commercial',
    pacing: 'balanced',
    tension: 'medium',
    dialogue: 'balanced',
    description: 'moderate',
  },
};

const defaultStyle: StyleProfile = {
  prose: 'commercial',
  pacing: 'balanced',
  tension: 'medium',
  dialogue: 'balanced',
  description: 'moderate',
};

export function CreativeStyleSettings({
  styleProfile,
  onChange,
  disabled = false,
}: CreativeStyleSettingsProps) {
  const [localStyle, setLocalStyle] = useState<StyleProfile>(
    styleProfile || defaultStyle
  );
  const [showCustomNotes, setShowCustomNotes] = useState(
    localStyle.prose === 'custom'
  );

  useEffect(() => {
    if (styleProfile) {
      setLocalStyle(styleProfile);
      setShowCustomNotes(styleProfile.prose === 'custom');
    }
  }, [styleProfile]);

  const handleChange = (
    field: keyof StyleProfile,
    value: string
  ) => {
    const newStyle = { ...localStyle, [field]: value };
    
    // Show custom notes when prose is set to custom
    if (field === 'prose') {
      setShowCustomNotes(value === 'custom');
    }
    
    setLocalStyle(newStyle);
    onChange(newStyle);
  };

  const applyPreset = (presetName: string) => {
    const preset = STYLE_PRESETS[presetName];
    if (preset) {
      setLocalStyle(preset);
      setShowCustomNotes(preset.prose === 'custom');
      onChange(preset);
    }
  };

  const SelectField = ({
    label,
    field,
    options,
  }: {
    label: string;
    field: keyof StyleProfile;
    options: { value: string; label: string }[];
  }) => (
    <div className="space-y-1">
      <Label className="text-muted-foreground text-sm">{label}</Label>
      <RadixSelect
        value={localStyle[field] as string}
        onValueChange={(value) => handleChange(field, value)}
        disabled={disabled}
      >
        <SelectTrigger className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </RadixSelect>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Presets */}
      <div className="space-y-2">
        <Label className="text-muted-foreground">Quick Presets</Label>
        <div className="flex flex-wrap gap-2">
          {Object.keys(STYLE_PRESETS).map((presetName) => (
            <Button
              key={presetName}
              variant="secondary"
              size="sm"
              onClick={() => applyPreset(presetName)}
              disabled={disabled}
              className="capitalize"
            >
              {presetName}
            </Button>
          ))}
        </div>
      </div>

      {/* Style Options Grid */}
      <div className="grid grid-cols-2 gap-4">
        <SelectField
          label="Prose Style"
          field="prose"
          options={[
            { value: 'literary', label: 'Literary' },
            { value: 'commercial', label: 'Commercial' },
            { value: 'thriller', label: 'Thriller' },
            { value: 'cozy', label: 'Cozy' },
            { value: 'custom', label: 'Custom' },
          ]}
        />

        <SelectField
          label="Pacing"
          field="pacing"
          options={[
            { value: 'slow-burn', label: 'Slow Burn' },
            { value: 'balanced', label: 'Balanced' },
            { value: 'fast-paced', label: 'Fast-Paced' },
          ]}
        />

        <SelectField
          label="Tension Level"
          field="tension"
          options={[
            { value: 'low', label: 'Low' },
            { value: 'medium', label: 'Medium' },
            { value: 'high', label: 'High' },
          ]}
        />

        <SelectField
          label="Dialogue Density"
          field="dialogue"
          options={[
            { value: 'sparse', label: 'Sparse' },
            { value: 'balanced', label: 'Balanced' },
            { value: 'dialogue-heavy', label: 'Dialogue-Heavy' },
          ]}
        />

        <SelectField
          label="Description Level"
          field="description"
          options={[
            { value: 'minimal', label: 'Minimal' },
            { value: 'moderate', label: 'Moderate' },
            { value: 'rich', label: 'Rich' },
          ]}
        />
      </div>

      {/* Custom Notes (shown when prose is 'custom') */}
      {showCustomNotes && (
        <div className="space-y-2">
          <Label className="text-muted-foreground">Custom Style Notes</Label>
          <Textarea
            value={localStyle.customNotes || ''}
            onChange={(e) => handleChange('customNotes', e.target.value)}
            placeholder="Describe your preferred writing style..."
            disabled={disabled}
            rows={3}
            className="resize-none"
          />
          <p className="text-muted-foreground text-xs">
            These notes will be included in the AI's context for every writing session.
          </p>
        </div>
      )}

      {/* Current Style Summary */}
      <div className="rounded-md border border-border bg-muted/30 p-3">
        <Label className="text-muted-foreground text-sm">Current Style Summary</Label>
        <p className="text-foreground mt-1 text-sm">
          {localStyle.prose} prose, {localStyle.pacing} pacing, {localStyle.tension} tension, {localStyle.dialogue} dialogue, {localStyle.description} descriptions
        </p>
      </div>
    </div>
  );
}

export default CreativeStyleSettings;
